import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus, CheckCircle2, Loader2, AlertCircle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

interface SearchResult {
  id: string;
  club_name: string;
  course_name: string;
  location?: { city?: string; state?: string; country?: string; address?: string };
}

interface CourseSearchModalProps {
  open: boolean;
  onClose: () => void;
  onImported: (courseId: string, courseName: string) => void;
  initialQuery?: string;
}

export function CourseSearchModal({ open, onClose, onImported, initialQuery = '' }: CourseSearchModalProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
  const [importError, setImportError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoSearched = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) return;
    setSearching(true);
    setSearchError(null);
    setResults([]);
    setImportError(null);
    try {
      const res = await fetch(`/api/courses/api-search?q=${encodeURIComponent(trimmed)}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message ?? 'Search failed');
      }
      const data = await res.json();
      setResults(data.results ?? []);
      if ((data.results ?? []).length === 0) {
        setSearchError('No courses found. Try just the club name (e.g. "Kenwood" not "Kenwood golf").');
      }
    } catch (err: any) {
      setSearchError(err.message ?? 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    runSearch(query);
  };

  const handleQueryChange = (v: string) => {
    setQuery(v);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (v.trim().length >= 2) {
      debounceTimer.current = setTimeout(() => runSearch(v), 600);
    } else {
      setResults([]);
      setSearchError(null);
    }
  };

  const handleImport = async (result: SearchResult) => {
    setImportingId(result.id);
    setImportError(null);
    try {
      const res = await apiRequest('POST', '/api/courses/api-import', { apiCourseId: result.id });
      const data = await res.json();
      if (!data.success) throw new Error(data.message ?? 'Import failed');
      if (data.teesImported === 0) {
        setImportError(`"${data.courseName}" was found but had no valid 18-hole tee data. You may need to add it manually.`);
        setImportingId(null);
        return;
      }
      setImportedIds((prev) => new Set(prev).add(result.id));
      await queryClient.invalidateQueries({ queryKey: ['/api/courses/local'] });
      onImported(data.courseId, data.courseName);
    } catch (err: any) {
      setImportError(err.message ?? 'Import failed. Please try again.');
    } finally {
      setImportingId(null);
    }
  };

  const handleClose = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setQuery('');
    setResults([]);
    setSearchError(null);
    setImportError(null);
    hasAutoSearched.current = false;
    onClose();
  };

  // Auto-search when opened with a pre-filled query
  useEffect(() => {
    if (open && initialQuery.trim().length >= 2 && !hasAutoSearched.current) {
      hasAutoSearched.current = true;
      setQuery(initialQuery);
      runSearch(initialQuery);
    }
  }, [open, initialQuery, runSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, []);

  const locationLabel = (r: SearchResult) => {
    const parts = [r.location?.city, r.location?.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : (r.location?.country ?? '');
  };

  const displayName = (r: SearchResult) => {
    if (r.club_name === r.course_name || !r.course_name) return r.club_name;
    return `${r.club_name} — ${r.course_name}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-sm w-full p-0 gap-0 rounded-2xl overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-gray-100">
          <DialogTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#064E3B]" />
            Find a Course
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 space-y-3">
          {/* Search input */}
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Club name (e.g. Kenwood, TPC Potomac…)"
              className="h-9 text-sm flex-1"
              data-testid="input-course-search"
              autoFocus
            />
            <Button
              onClick={handleSearch}
              disabled={searching || query.trim().length < 2}
              className="h-9 px-3 bg-[#064E3B] hover:bg-[#065f46] text-white text-xs"
              data-testid="button-course-search"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
          </div>

          {/* Hint */}
          {results.length === 0 && !searchError && !searching && query.trim().length === 0 && (
            <p className="text-xs text-gray-400">
              Tip: use just the club name — "Kenwood" finds it faster than "Kenwood golf and country club".
            </p>
          )}

          {/* Error states */}
          {searchError && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{searchError}</span>
            </div>
          )}
          {importError && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{importError}</span>
            </div>
          )}

          {/* Results list */}
          {results.length > 0 && (
            <div className="space-y-1 max-h-64 overflow-y-auto -mx-1 px-1">
              {results.map((r) => {
                const alreadyImported = importedIds.has(r.id);
                const isImporting = importingId === r.id;
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    data-testid={`course-result-${r.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{displayName(r)}</p>
                      {locationLabel(r) && (
                        <p className="text-xs text-gray-500 truncate">{locationLabel(r)}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleImport(r)}
                      disabled={isImporting || alreadyImported}
                      className={`flex-shrink-0 h-7 px-2 text-xs ${
                        alreadyImported
                          ? 'bg-emerald-600 hover:bg-emerald-600 text-white'
                          : 'bg-[#064E3B] hover:bg-[#065f46] text-white'
                      }`}
                      data-testid={`button-import-course-${r.id}`}
                    >
                      {isImporting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : alreadyImported ? (
                        <CheckCircle2 className="w-3 h-3" />
                      ) : (
                        <Plus className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Searching state */}
          {searching && results.length === 0 && (
            <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching ~30,000 courses…
            </div>
          )}

          {/* Empty state before search */}
          {results.length === 0 && !searchError && !searching && query.trim().length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">
              Search ~30,000 courses worldwide
            </p>
          )}
        </div>

        <div className="px-4 pb-4">
          <Button
            variant="ghost"
            className="w-full h-8 text-xs text-gray-500"
            onClick={handleClose}
            data-testid="button-course-search-close"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
